"use client";

import React from "react";
import { motion } from "framer-motion";
import {
  MapIcon,
  AcademicCapIcon,
  UsersIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";

const reasons = [
  {
    title: "Break Away to Break Through",
    subtitle: "Escape routines and explore what's possible.",
    description:
      "Ditch the usual. Join curated trips with deep meaning, designed for change-makers, dreamers, and doers.",
    icon: MapIcon,
  },
  {
    title: "Learn & Level Up",
    subtitle: "Growth you can feel – not just scroll past.",
    description:
      "Travel with mentors who’ll guide you through real conversations, mindset shifts, and clarity-defining moments.",
    icon: AcademicCapIcon,
  },
  {
    title: "Find Your Tribe",
    subtitle: "Your people are already out there – we bring you together.",
    description:
      "Connect with a diverse crew of 50+ backgrounds. Collaborate, grow, and stay connected long after the trip ends.",
    icon: UsersIcon,
  },
];

const textVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: "easeOut",
    },
  }),
};

const WhyJoinUs = () => {
  return (
    <section className=" py-10 lg:py-10 px-4 sm:px-6 lg:px-20 relative overflow-hidden">
      <div className="container mx-auto relative z-10">
        <div className="text-center mb-16">
          <motion.h1
            className="font-bricolage text-[#FFB61A] font-black text-4xl md:text-6xl tracking-tight mb-6"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Why Join <span className="text-[#0D0D0D] underline decoration-[#FFB61A]">Outcome Outing?</span>
          </motion.h1>

          <motion.h2
            className="font-bricolage font-semibold text-[#1d1d1d] text-lg md:text-2xl italic opacity-80 max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Top reasons why thousands choose clarity over ordinary.
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {reasons.map((reason, index) => {
            const IconComponent = reason.icon || QuestionMarkCircleIcon;
            return (
              <motion.div
                key={reason.title}
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={textVariant}
                whileHover={{ y: -8 }}
                className="group relative bg-white border border-gray-100 rounded-[32px] p-8 h-full flex flex-col
                  shadow-[inset_0_4px_12px_rgba(0,0,0,0.05),0_10px_30px_-10px_rgba(0,0,0,0.1)] 
                  transition-all duration-500 hover:shadow-[0_20px_40px_rgba(255,182,26,0.15)]"
              >
                <div className="mb-6 flex items-center justify-center w-14 h-14 bg-[#FFB61A]/10 rounded-2xl shadow-inner">
                  <IconComponent className="h-7 w-7 text-[#FFB61A]" />
                </div>

                <div className="space-y-4">
                  <h3 className="font-bricolage font-bold text-xl text-[#0D0D0D] group-hover:text-[#FFB61A] transition-colors">
                    {reason.title}
                  </h3>
                  <p className="font-jakarta font-bold text-[#FFB61A] text-sm uppercase tracking-wider">
                    {reason.subtitle}
                  </p>
                  <p className="font-jakarta text-gray-600 text-base leading-relaxed">
                    {reason.description}
                  </p>
                </div>
                
                <div className="mt-8 pt-6 border-t border-gray-50 flex items-center">
                   <div className="h-1.5 w-12 bg-[#FFB61A] rounded-full shadow-inner" />
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="w-full flex justify-center mt-20">
          <button className="cursor-pointer relative bg-[#063258] py-2 rounded-full min-w-[12rem] min-h-[3.5rem] group flex items-center justify-start hover:bg-[#FFB619] transition-all duration-700 ease-in-out shadow-[inset_1px_2px_5px_rgba(0,0,0,0.5)]">
            <a
              href="https://chat.whatsapp.com/C3SkpzYbUoj037GX3cPpsx"
              target="_blank"
              rel="noopener noreferrer"
              className="absolute inset-0 z-10"
            />
            <div className="absolute flex px-1 py-1 justify-start items-center inset-0">
              <div className="rounded-full shrink-0 flex justify-center items-center shadow-[inset_1px_-1px_3px_0_black] h-full aspect-square bg-[#FFB619] transition-all duration-700 group-hover:bg-black group-hover:translate-x-[8.2rem]">
                <div className="size-[1rem] text-black group-hover:text-white group-hover:-rotate-45 transition-all duration-700">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16" height="100%" width="100%">
                    <path fill="currentColor" d="M12.175 9H0V7H12.175L6.575 1.4L8 0L16 8L8 16L6.575 14.6L12.175 9Z" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="pl-16 pr-6 font-bricolage font-bold group-hover:translate-x-[-2.5rem] transition-all duration-700 group-hover:text-black text-white text-lg">
              Join Community
            </div>
          </button>
        </div>
      </div>
    </section>
  );
};

export default WhyJoinUs;